/**
 * useAdrForm - store-direct form logic for ADR editing.
 *
 * Reads from the ADR store, writes directly to the ADR store.
 * No local state duplication — the store IS the source of truth.
 *
 * All store-write callbacks use a ref for adrId to avoid stale closures.
 * BlockNote editors are ref-based: editor content is flushed to
 * the store on onChange (debounced 300ms) and on explicit flush().
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdrById, useAdrActions } from '../stores';
import { getAdrById } from './adrStore';
import type { ADR, AdrStatus, AdrImplementationStatus, AdrPerson, AdrCommentNode } from '../types';

const EDITOR_FLUSH_DEBOUNCE_MS = 300;

export interface AdrForm {
  title: string;
  setTitle: (v: string) => void;
  status: AdrStatus;
  setStatus: (v: AdrStatus) => void;
  implStatus: AdrImplementationStatus;
  setImplStatus: (v: AdrImplementationStatus) => void;
  scopeType: 'global' | 'board' | 'canvas';
  setScopeType: (v: 'global' | 'board' | 'canvas') => void;
  people: AdrPerson[];
  setPeople: (v: AdrPerson[]) => void;

  context: unknown[];
  decision: unknown[];
  consequences: unknown[];
  editorKey: number;
  setContextEditor: (editor: unknown) => void;
  setDecisionEditor: (editor: unknown) => void;
  setConsequencesEditor: (editor: unknown) => void;
  onEditorChange: () => void;

  loadedAdrId: string | null;
  existingAdr: ADR | undefined;
  canSave: boolean;

  flushEditors: () => void;
  deleteAdr: () => boolean;

  handleAddComment: (text: string, authorEmployeeId: string, parentCommentId: string | null) => void;
  handleResolveComment: (commentId: string) => void;
  handleUnresolveComment: (commentId: string) => void;
}

export function useAdrForm(adrId?: string): AdrForm {
  const { t } = useTranslation();
  const adr = useAdrById(adrId || '');
  const actions = useAdrActions();

  // ---- Ref for adrId — all callbacks read from here, never stale ----
  const adrIdRef = useRef(adrId);
  adrIdRef.current = adrId;

  // ---- BlockNote editor refs + key ----
  const contextEditorRef = useRef<any>(null);
  const decisionEditorRef = useRef<any>(null);
  const consequencesEditorRef = useRef<any>(null);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    contextEditorRef.current = null;
    decisionEditorRef.current = null;
    consequencesEditorRef.current = null;
    setEditorKey(k => k + 1);
  }, [adrId]);

  const setContextEditor = useCallback((editor: unknown) => { contextEditorRef.current = editor; }, []);
  const setDecisionEditor = useCallback((editor: unknown) => { decisionEditorRef.current = editor; }, []);
  const setConsequencesEditor = useCallback((editor: unknown) => { consequencesEditorRef.current = editor; }, []);

  // ---- Title: local buffer for controlled input + write-through ----
  const [localTitle, setLocalTitle] = useState(adr?.title ?? '');

  const prevAdrIdForTitle = useRef(adrId);
  if (prevAdrIdForTitle.current !== adrId) {
    prevAdrIdForTitle.current = adrId;
    setLocalTitle(adr?.title ?? '');
  }

  // ---- Store writes (all use adrIdRef — never stale) ----

  const update = useCallback((updates: Partial<Omit<ADR, 'id' | 'createdAt'>>) => {
    const id = adrIdRef.current;
    if (!id) return;
    actions.updateAdr(id, updates);
  }, [actions]);

  const setTitle = useCallback((v: string) => {
    setLocalTitle(v);
    update({ title: v });
  }, [update]);

  const setStatus = useCallback((v: AdrStatus) => update({ status: v }), [update]);
  const setImplStatus = useCallback((v: AdrImplementationStatus) => update({ implementationStatus: v }), [update]);
  const setPeople = useCallback((v: AdrPerson[]) => update({ people: v }), [update]);

  const setScopeType = useCallback((v: 'global' | 'board' | 'canvas') => {
    const id = adrIdRef.current;
    if (!id) return;
    const current = getAdrById(id);
    if (!current) return;
    const boardId = current.scope.type !== 'global' ? (current.scope as any).boardId : '';
    const treeId = current.scope.type === 'canvas' ? (current.scope as any).treeId : '';
    let scope: ADR['scope'];
    if (v === 'global') scope = { type: 'global' };
    else if (v === 'board') scope = { type: 'board', boardId };
    else scope = { type: 'canvas', boardId, treeId };
    update({ scope });
  }, [update]);

  // ---- Editor content → store (debounced) ----

  const editorFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushEditors = useCallback(() => {
    if (editorFlushTimerRef.current) clearTimeout(editorFlushTimerRef.current);
    const id = adrIdRef.current;
    if (!id) return;
    const updates: Partial<Omit<ADR, 'id' | 'createdAt'>> = {};
    if (contextEditorRef.current) updates.context = contextEditorRef.current.document;
    if (decisionEditorRef.current) updates.decision = decisionEditorRef.current.document;
    if (consequencesEditorRef.current) updates.consequences = consequencesEditorRef.current.document;
    if (Object.keys(updates).length > 0) {
      actions.updateAdr(id, updates);
    }
  }, [actions]);

  const onEditorChange = useCallback(() => {
    if (editorFlushTimerRef.current) clearTimeout(editorFlushTimerRef.current);
    editorFlushTimerRef.current = setTimeout(flushEditors, EDITOR_FLUSH_DEBOUNCE_MS);
  }, [flushEditors]);

  useEffect(() => {
    return () => {
      if (editorFlushTimerRef.current) clearTimeout(editorFlushTimerRef.current);
    };
  }, [adrId]);

  // ---- Delete ----

  const deleteAdr = useCallback((): boolean => {
    const id = adrIdRef.current;
    if (!id) return false;
    if (!confirm(t('adr.deleteConfirm'))) return false;
    actions.deleteAdr(id);
    return true;
  }, [actions, t]);

  // ---- Comment handlers ----

  const handleAddComment = useCallback((text: string, authorEmployeeId: string, parentCommentId: string | null) => {
    const id = adrIdRef.current;
    if (!id) return;
    const comment: AdrCommentNode = {
      id: `cmt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      authorEmployeeId,
      text,
      resolved: false,
      children: [],
      createdAt: new Date().toISOString(),
    };
    actions.addComment(id, comment, parentCommentId);
  }, [actions]);

  const handleResolveComment = useCallback((commentId: string) => {
    const id = adrIdRef.current;
    if (!id) return;
    actions.resolveComment(id, commentId);
  }, [actions]);

  const handleUnresolveComment = useCallback((commentId: string) => {
    const id = adrIdRef.current;
    if (!id) return;
    actions.unresolveComment(id, commentId);
  }, [actions]);

  return {
    title: localTitle,
    setTitle,
    context: adr?.context ?? [],
    decision: adr?.decision ?? [],
    consequences: adr?.consequences ?? [],
    editorKey,
    setContextEditor, setDecisionEditor, setConsequencesEditor,
    onEditorChange,
    status: adr?.status ?? 'draft',
    setStatus,
    implStatus: adr?.implementationStatus ?? 'pending',
    setImplStatus,
    scopeType: adr?.scope.type ?? 'global',
    setScopeType,
    people: adr?.people ?? [],
    setPeople,
    loadedAdrId: adrId ?? null,
    existingAdr: adr,
    canSave: localTitle.trim() !== '',
    flushEditors,
    deleteAdr,
    handleAddComment,
    handleResolveComment,
    handleUnresolveComment,
  };
}
